import React from 'react'
import { isMobileSize } from '../utils'
import { storage } from '../storage'

/**
 * The `useBufferedUpdates` hook is used to debounce updates to a value, without unnecessarily
 * setting the internal value when it is not needed. This provides a smoother user experience
 *
 * @param externalValue - The value to be updated
 * @param setExternal - The function to set the external value
 * @param valueUpdateDelay - The delay in milliseconds before the external value is updated, after a change in the internal value
 * @param emitOnExternalUpdate - If true, the external value will be updated immediately when it changes. Thought this seems
 * pointless, it allows you to hook into the external value change and do something with it. For example, the quill editor
 * uses the html as external value but needs to re-emit when it changes to update the delta as well.
 */
export const useBufferedUpdates = <T>(
  externalValue: T,
  setExternal: (value: T) => void,
  valueUpdateDelay: number,
  emitOnExternalUpdate?: boolean
) => {
  const [internalValue, setInternalValue] = React.useState<T>(externalValue)
  const expectedUpdates = React.useRef<T[]>([])

  const lastExternalValue = React.useRef<T | null>(null)
  React.useEffect(() => {
    // we dont want this effect to trigger twice in strict mode, because the updates are only added once either
    // way and we will think updates are unexpected when we shouldve expected them
    if (lastExternalValue.current === externalValue) return
    lastExternalValue.current = externalValue

    if (expectedUpdates.current.length > 0 && expectedUpdates.current[0] === externalValue) {
      // we expected this update because it is just a change caused by our own "setExternal" call
      // so we can just ignore it. Essentially this is a change from inside the component to outside
      // being reflected back to inside. If we did setInternalValue here, we could erase all user behavior
      // between our own setExternal and this effect
      expectedUpdates.current.shift()
    } else {
      // in this case, this update is something we didn't expect, so we need to actually apply it
      expectedUpdates.current = []
      setInternalValue(externalValue)
      if (emitOnExternalUpdate) emitAndExpectUpdate(externalValue)
    }
  }, [externalValue])

  const delayedSetExternalTimeout = React.useRef<NodeJS.Timeout | null>(null)
  // Stop the timeout on unmount
  React.useEffect(() => {
    return () => {
      delayedSetExternalTimeout.current && clearTimeout(delayedSetExternalTimeout.current)
    }
  }, [])

  const emitAndExpectUpdate = (v: T) => {
    if (delayedSetExternalTimeout.current) clearTimeout(delayedSetExternalTimeout.current)
    delayedSetExternalTimeout.current = setTimeout(() => {
      setExternal(v)
      expectedUpdates.current.push(v)
    }, valueUpdateDelay)
  }

  const setInternalValueAndEmit = (v: T) => {
    setInternalValue(v)
    emitAndExpectUpdate(v)
  }

  return [internalValue, setInternalValueAndEmit] as const
}

/**
 * The `useAskBeforeLeavingPage` hook is used to prompt the user with a confirmation dialog
 * when they attempt to leave the page, if the `shouldWarn` parameter is true.
 * The message displayed in the dialog can be customized with the `message` parameter.
 * @param shouldWarn - A boolean indicating whether to show the warning dialog or not.
 * @param message - The message to display in the confirmation dialog.
 */
export const useAskBeforeLeavingPage = (
  shouldWarn: boolean,
  message = 'You have unsaved changes, are you sure you want to leave?'
) => {
  React.useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!shouldWarn) return

      event.preventDefault()
      event.returnValue = message // For modern browsers
      return message // For legacy browsers
    }

    // Add event listener
    window.addEventListener('beforeunload', handleBeforeUnload)

    // Cleanup event listener on unmount
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [shouldWarn, message])
}

/**
 * The `useAnimationFrame` hook is used to run a callback function on each animation frame.
 * It takes a callback function and a boolean `activate` parameter. If `activate` is true,
 * the callback will be called on each animation frame, otherwise it will not.
 * The callback receives the delta time since the last frame in seconds.
 * @param callback - The callback function to be called on each animation frame.
 * @param activate - A boolean indicating whether to activate the animation frame or not.
 */
export const useAnimationFrame = (callback: (deltaTime: number) => void, activate: boolean) => {
  const requestRef = React.useRef<number>(0)
  const previousTimeRef = React.useRef<number | undefined>(undefined)

  const animate = (time: number) => {
    if (previousTimeRef.current !== undefined) {
      const deltaTime = time - previousTimeRef.current
      callback(deltaTime / 1000)
    }
    previousTimeRef.current = time
    if (activate) requestRef.current = requestAnimationFrame(animate)
    else previousTimeRef.current = undefined
  }

  React.useEffect(() => {
    if (activate) requestRef.current = requestAnimationFrame(animate)
    return () => {
      cancelAnimationFrame(requestRef.current)
      previousTimeRef.current = undefined
    }
  }, [activate])
}

/**
 * Debounces the callback by the given delay. The first call is made immediately.
 * If a new call is made before the delay has passed since the last call,
 * the new call is ignored.
 * @param callback - The callback function to be debounced.
 * @param delay - The delay in milliseconds before the callback is called.
 * @returns A debounced version of the callback function.
 */
export const useDebounceLeading = <T = void>(callback: (args: T) => void, delay: number) => {
  const lastCallTimeRef = React.useRef<number>(0)
  const debouncedCallback = React.useCallback(
    (args: T) => {
      const now = Date.now()
      if (now - lastCallTimeRef.current > delay) {
        callback(args)
        lastCallTimeRef.current = now
      }
    },
    [callback, delay]
  )
  return debouncedCallback
}

/**
 * Debounces the callback by the given delay, but if a new call is made before the delay has passed,
 * the delay is reset.
 * @param callback - The callback function to be debounced.
 * @param delay - The delay in milliseconds before the callback is called.
 * @returns A debounced version of the callback function.
 */
export const useDebounce = <T = void>(callback: (args: T) => void, delay: number) => {
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null)

  const debouncedCallback = React.useCallback(
    (args: T) => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      timeoutRef.current = setTimeout(() => {
        callback(args)
        timeoutRef.current = null
      }, delay)
    },
    [callback, delay]
  )

  return debouncedCallback
}

/**
 * The `useLocalStorageState` hook is used to manage a state that is saved to
 * localStorage. It takes a key and a default value as parameters.
 * The state is initialized with the value from localStorage if it exists, otherwise
 * it uses the default value. The state is updated in localStorage whenever it changes.
 * @param key - The key to store the value in localStorage.
 * @param defaultValue - The default value to use if the key does not exist in localStorage.
 * @returns A tuple containing the current value and a function to update it, like useState.
 */
export const useLocalStorageState = <T>(key: string, defaultValue: T) => {
  const [value, setValue] = React.useState<T>(() => {
    const storedValue = storage.local.get(key)
    return storedValue ? JSON.parse(storedValue) : defaultValue
  })

  React.useEffect(() => {
    storage.local.set(key, JSON.stringify(value))
  }, [key, value])

  return [value, setValue] as const
}

/**
 * The `useIsMobile` hook is used to determine if the current screen size is mobile and trigger a re-render when it changes.
 */
export const useIsMobile = () => {
  const [isMobile, setIsMobile] = React.useState(isMobileSize())

  React.useEffect(() => {
    const handleResize = () => setIsMobile(isMobileSize())
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return isMobile
}
